import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useCardsQuery, type CardOut, type CardState } from "@/api/cards";
import { useKpiItems, useKpiSnapshot, type KpiEntry } from "@/api/kpi";
import { useOzet } from "@/api/ozet";
import { AlarmManager } from "@/components/AlarmManager";
import { ChartCard } from "@/components/ChartCard";
import { DeltaModeToggle } from "@/components/DeltaModeToggle";
import { EmptyState } from "@/components/EmptyState";
import { Freshness } from "@/components/Freshness";
import { KpiCard, type AlarmLevel } from "@/components/KpiCard";
import { KpiPeriodSelect } from "@/components/KpiPeriodSelect";
import { SourceHealth } from '@/components/SourceHealth';
import { SectionHeader } from "@/components/ui";
import { alarmFromKpis } from "@/features/kpi/alarm";
import { applyPeriodFilter } from "@/features/kpi/periodFilter";
import { AiAnalyzeModal } from "@/features/ozet/AiAnalyzeModal";
import { AiBriefPanel } from "@/features/ozet/AiBriefPanel";
import { BugunSeridi } from "@/features/ozet/BugunSeridi";
import { CiroTrendi } from "@/features/ozet/CiroTrendi";
import { DestekIadeModal } from "@/features/ozet/DestekIadeModal";
import { EncokSatanTablosu } from "@/features/ozet/EncokSatanTablosu";
import { GidisatHero } from "@/features/ozet/GidisatHero";
import { HealthRingPopover } from "@/features/ozet/HealthRingPopover";
import { KargoSirketiGrid, type KargoSirketiOge } from "@/features/ozet/KargoSirketiGrid";
import { KpiDetailModal } from "@/features/ozet/KpiDetailModal";
import { SghvKartlari } from "@/features/ozet/SghvKartlari";
import { TeslimatChart, TeslimatHaftaModal, TeslimatIstatistikCipleri } from "@/features/ozet/TeslimatHaftaModal";
import { useTeslimatHaftalik, type TeslimatHaftaSecimi } from "@/features/ozet/teslimat";
import { YorumOzetKarti } from "@/features/ozet/YorumOzetKarti";
import { useDeltaMode } from "@/hooks/useDeltaMode";
import { useKpiPeriod } from "@/hooks/useKpiPeriod";
import { usePeriod } from "@/hooks/usePeriod";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

const ALARM_LEVEL_MAP: Record<string, AlarmLevel> = { critical: "critical", warning: "warning", ok: "normal" };

interface AnalyzeTarget {
  id: string;
  title: string;
  rows?: KpiEntry[];
  prompt?: string;
}

function latestFreshness(tazelik: { last_sync: string | null }[] | undefined): string | null {
  if (!tazelik || tazelik.length === 0) return null;
  let latest: string | null = null;
  for (const row of tazelik) {
    if (!row.last_sync) continue;
    if (!latest || row.last_sync > latest) latest = row.last_sync;
  }
  return latest;
}

function asEntry(v: unknown): KpiEntry | undefined {
  return v && typeof v === "object" ? (v as KpiEntry) : undefined;
}

function entryNum(v: unknown, key: string): number | undefined {
  const entry = asEntry(v);
  const x = entry?.[key];
  return typeof x === "number" ? x : undefined;
}

function entryAlarm(v: unknown): string | undefined {
  const entry = asEntry(v);
  return entry && typeof entry.alarm_level === "string" ? entry.alarm_level : undefined;
}

/** Operasyon kartlarinin alt satiri: "3 kritik · 5 uyari" (yoksa bos). */
function kartAltSatiri(entry: KpiEntry | undefined): string | undefined {
  const critical = entryNum(entry, "critical_count") ?? 0;
  const warning = entryNum(entry, "warning_count") ?? 0;
  const parcalar: string[] = [];
  if (critical > 0) parcalar.push(`${fmtInt(critical)} ${t("ozet.kargo_sirketi.kritik")}`);
  if (warning > 0) parcalar.push(`${fmtInt(warning)} ${t("ozet.kargo_sirketi.uyari")}`);
  return parcalar.length > 0 ? parcalar.join(" · ") : undefined;
}

/**
 * Gidişat özeti — eski ana sayfanın bölüm sırası ve içeriği, yeni tasarım
 * diliyle (docs/arayuz-tasarim-rehberi.md):
 *  1) sağlık kartı (halka + hüküm + tazelik)
 *  2) E-Ticaret Gidişatı: hüküm şeridi + 4 blok + günlük ciro trendi (tek kart)
 *  3) AI Gidişat Notu (katlanabilir)
 *  4) Bugün şeridi
 *  5) Uçtan uca teslimat süresi
 *  6) Operasyonel durum (kuyruk dönemi seçici burada)
 *  7) Kargo şirketi dağılımı · 8) SGHV · 9) Yorumlar · 10) Saatlik satış · 11) En çok satan
 */
export function Ozet() {
  const { period, dateRange } = usePeriod();
  const { kpiPeriod } = useKpiPeriod();
  const { deltaMode, setDeltaMode } = useDeltaMode();
  const queryClient = useQueryClient();

  const ozetQuery = useOzet(period, dateRange);
  const cardsQuery = useCardsQuery();
  const snapshotQuery = useKpiSnapshot();
  const teslimatQuery = useTeslimatHaftalik();
  const saatlikQuery = useKpiItems("saatlik_satis");

  const [detailKpi, setDetailKpi] = useState<{ id: string; title: string } | null>(null);
  const [analyzeTarget, setAnalyzeTarget] = useState<AnalyzeTarget | null>(null);
  const [destekIadeOpen, setDestekIadeOpen] = useState(false);
  const [teslimatHaftaSecimi, setTeslimatHaftaSecimi] = useState<TeslimatHaftaSecimi | null>(null);

  const allCards: CardOut[] = cardsQuery.data?.cards ?? [];
  const cardById = (id: string): CardOut | undefined => allCards.find((c) => c.id === id);

  const kpis = snapshotQuery.data?.kpis ?? null;
  const filtered = useMemo(() => (kpis ? applyPeriodFilter(kpis, kpiPeriod) : null), [kpis, kpiPeriod]);
  const health = filtered?.healthScore ?? snapshotQuery.data?.health_score ?? null;
  const breakdown = filtered?.healthBreakdown ?? [];
  const alarm = useMemo(() => alarmFromKpis(filtered?.kpis ?? kpis), [filtered, kpis]);

  const lastSuccessAt = latestFreshness(ozetQuery.data?.veri_kalitesi.tazelik);

  function handleRefresh() {
    void queryClient.invalidateQueries({ queryKey: ["ozet"] });
    void queryClient.invalidateQueries({ queryKey: ["kpi"] });
    void cardsQuery.refetch();
  }

  function openDetail(id: string, title: string) {
    setDetailKpi({ id, title });
  }

  function openAnalyze(id: string, title: string) {
    setAnalyzeTarget({ id, title });
  }

  function askFollowUp(question: string) {
    setAnalyzeTarget({ id: "gidisat", title: t("ozet.ai.baslik"), prompt: question });
  }

  /** Sabit sıralı operasyon kartı: `not_supported`/yok → gizli. */
  function renderOpKpiCard(id: string) {
    const card = cardById(id);
    if (!card || card.state === "not_supported") return null;
    const entry = asEntry(filtered?.kpis[id]);
    const count = entryNum(entry, "count");
    const alarmVal = entryAlarm(entry);
    return (
      <KpiCard
        key={id}
        title={card.title}
        state={card.state}
        reason={card.reason}
        value={card.state === "live" ? (count ?? null) : null}
        sub={card.state === "live" ? kartAltSatiri(entry) : undefined}
        alarmLevel={alarmVal ? (ALARM_LEVEL_MAP[alarmVal] ?? "normal") : "normal"}
        setupHref="/kurulum"
        onOpenDetail={card.state === "live" ? () => openDetail(id, card.title) : undefined}
        onAnalyze={card.state === "live" ? () => openAnalyze(id, card.title) : undefined}
      />
    );
  }

  /** "Cevap bekleyen destek + iade" — iki kartın birleşimi, iki sekmeli modal. */
  function renderDestekIadeCard() {
    const destekCard = cardById("destek_talepleri");
    const iadeCard = cardById("bekleyen_iade");
    if (!destekCard && !iadeCard) return null;

    const state: CardState =
      destekCard?.state === "live" || iadeCard?.state === "live"
        ? "live"
        : destekCard?.state === "not_connected" || iadeCard?.state === "not_connected"
          ? "not_connected"
          : "not_supported";
    if (state === "not_supported") return null;

    const reason = destekCard?.state === "not_connected" ? destekCard.reason : (iadeCard?.reason ?? null);
    const entry = asEntry(filtered?.kpis.destek_iade_talepleri);
    const count = entryNum(entry, "count");
    const alarmVal = entryAlarm(entry);

    return (
      <KpiCard
        key="destek_iade_talepleri"
        title={t("ozet.destek_iade.baslik")}
        state={state}
        reason={reason}
        value={state === "live" ? (count ?? null) : null}
        sub={state === "live" ? kartAltSatiri(entry) : undefined}
        alarmLevel={alarmVal ? (ALARM_LEVEL_MAP[alarmVal] ?? "normal") : "normal"}
        setupHref="/kurulum"
        onOpenDetail={state === "live" ? () => setDestekIadeOpen(true) : undefined}
      />
    );
  }

  const opKpiNodes = [renderOpKpiCard("kargolanmamis"), renderOpKpiCard("kargo_yolda"), renderDestekIadeCard(), renderOpKpiCard("kritik_stok")].filter(Boolean);

  const kargoSirketiRaw = asEntry(filtered?.kpis.kargo_sirketi);
  const kargoSirketiCompanies: KargoSirketiOge[] = Array.isArray(kargoSirketiRaw?.by_cargo_company)
    ? (kargoSirketiRaw!.by_cargo_company as KargoSirketiOge[])
    : [];

  const musteriYorumlariCard = cardById("musteri_yorumlari");
  const musteriYorumlariEntry = asEntry(filtered?.kpis.musteri_yorumlari);

  const encokSatanRaw = asEntry(filtered?.kpis.encok_satan);
  const encokSatanItems: KpiEntry[] = Array.isArray(encokSatanRaw?.items) ? (encokSatanRaw!.items as KpiEntry[]) : [];

  const uyarilar = ozetQuery.data?.veri_kalitesi.uyarilar ?? [];

  return (
    <section id="asakai-ozet" className="space-y-6">
      <AlarmManager level={alarm.level} criticalCount={alarm.criticalCount} />
      <div className="overview-heading">
        <div><h1>Mağazanıza genel bakış</h1><p>Satışın gidişatı, bekleyen işler ve ürünleriniz.</p></div>
        <Freshness lastSuccessAt={lastSuccessAt} onRefreshed={handleRefresh} />
      </div>
      <SourceHealth />
      <nav className="overview-jumps" aria-label="Özet bölümleri"><a href="#satis-ozeti">Satış performansı</a><a href="#operasyon-ozeti">Operasyon</a><a href="#urun-ozeti">En çok satanlar</a></nav>

      {ozetQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
      {ozetQuery.isError && (
        <p role="alert" className="text-sm text-red">
          {t("ozet.hata")}
        </p>
      )}
      {snapshotQuery.isError && (
        <p role="alert" className="ribbon ribbon-warn text-sm">
          {t("kpi.snapshot_hata")}
        </p>
      )}

      {/* 2) E-Ticaret Gidişatı */}
      {ozetQuery.data && (
        <div id="satis-ozeti" className="scroll-mt-6">
          <SectionHeader
            title="Satışın gidişatı"
            note={dateRange ? `${dateRange.start.split('-').reverse().join('.')} – ${dateRange.end.split('-').reverse().join('.')}` : t("ozet.sec.gidisat_not", { n: String(period) })}
            actions={<DeltaModeToggle mode={deltaMode} onChange={setDeltaMode} />}
          />
          <div className="mt-4 space-y-4" data-testid="gidisat-karti">
            <GidisatHero data={ozetQuery.data} deltaMode={deltaMode} chart={<CiroTrendi data={ozetQuery.data} />} context={<div className="health-summary" data-testid="ozet-saglik-karti"><h3 className="mb-3 text-sm font-semibold">Operasyon sağlığı</h3><HealthRingPopover value={health} breakdown={breakdown} size={60} /><p className="mt-3 border-t border-line pt-3 text-xs text-muted">Açık iş kuyruklarından hesaplanır. Satış başarısı veya veri bağlantı skoru değildir.</p></div>} />
            {uyarilar.length > 0 && (
              <details className="text-xs text-muted">
                <summary className="cursor-pointer font-semibold hover:text-ink">ⓘ {t("ozet.veri_notlari", { n: String(uyarilar.length) })}</summary>
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  {uyarilar.map((u) => (
                    <li key={u}>{u}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      {/* 6) Operasyonel durum */}
      <div id="operasyon-ozeti" className="operation-section scroll-mt-6" data-testid="ozet-op-kpis">
        <SectionHeader title={t("ozet.sec.op")} note={t("ozet.sec.op_not")} actions={<KpiPeriodSelect />} />
        <details className="mt-2 text-xs text-muted"><summary className="cursor-pointer py-1">Kuyruk dönemi ve gecikmeler nasıl hesaplanıyor?</summary><p className="max-w-3xl pb-2 leading-relaxed">Güncel açık siparişler gösterilir. Kuyruk dönemi siparişin oluşturulduğu tarihe göre süzer; bugün dahil takvim günleri ve Türkiye saati kullanılır. Sayılar ürün veya koli adedi değil, sipariş adedidir. Tarih bilinmiyorsa gecikme alarmı hesaplanmaz.</p></details>
        {cardsQuery.isLoading && <p className="mt-3 text-sm text-muted">{t("common.loading")}</p>}
        {cardsQuery.isError && (
          <p role="alert" className="mt-3 text-sm text-red">
            {t("common.error")}
          </p>
        )}
        {!cardsQuery.isLoading && !cardsQuery.isError && opKpiNodes.length === 0 && (
          <div className="mt-3">
            <EmptyState kind="no_data" />
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">{opKpiNodes}</div>
        <div className="mt-4"><BugunSeridi kpis={filtered?.kpis ?? null} cards={allCards} onOpenDetail={openDetail} /></div>
      </div>

      {/* 11) En çok satan */}
      <div id="urun-ozeti" className="scroll-mt-6" data-testid="ozet-encok-satan">
        <SectionHeader title={t("ozet.sec.encok")} note="Bugün dahil son 7 takvim günü · sipariş kalemlerinden hesaplanır" />
        <div className="mt-3">{encokSatanItems.length > 0 ? <EncokSatanTablosu items={encokSatanItems} /> : <EmptyState kind="no_data" />}</div>
      </div>

      <details className="analysis-disclosure"><summary><span>Satış saatleri ve müşteri yorumları</span><span className="disclosure-hint">Analizi aç</span></summary><div className="mt-5">
      {/* 9) Yorumlar + 10) Saatlik satış */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div>
          <SectionHeader title={t("ozet.sec.yorum")} note={t("ozet.sec.yorum_not")} />
          <div className="mt-3">
            <YorumOzetKarti
              card={musteriYorumlariCard}
              entry={musteriYorumlariEntry}
              onOpenDetail={() => openDetail("musteri_yorumlari", musteriYorumlariCard?.title ?? t("ozet.yorum_ozet.baslik"))}
            />
          </div>
        </div>
        <div>
          <SectionHeader title={t("ozet.sec.saatlik")} note={t("ozet.sec.saatlik_not")} />
          <div className="mt-3">
            {saatlikQuery.data && saatlikQuery.data.rows.length > 0 ? (
              <ChartCard
                type="bar"
                valueKind="money"
                height={200}
                labelEvery={3}
                data={{
                  labels: saatlikQuery.data.rows.map((r) => String(r.hour ?? "")),
                  datasets: [
                    { label: t("ozet.bugun"), data: saatlikQuery.data.rows.map((r) => Number(r.today_revenue) || 0) },
                    { label: t("ozet.dun"), data: saatlikQuery.data.rows.map((r) => Number(r.yesterday_revenue) || 0) },
                  ],
                }}
              />
            ) : (
              <EmptyState kind="no_data" />
            )}
          </div>
        </div>
      </div>

      </div></details>

      {/* 3) AI Gidişat Notu */}
      <details className="analysis-disclosure"><summary><span>AI değerlendirmesi</span><span className="disclosure-hint">Son {period} tamamlanmış gün</span></summary><div className="mt-3"><AiBriefPanel days={period} onAskFollowUp={askFollowUp} /></div></details>

      <details className="analysis-disclosure"><summary><span>Teslimat ve stok analizi</span><span className="disclosure-hint">Analizi aç</span></summary><div className="mt-5 space-y-6">
      {/* 5) Teslimat */}
      <div>
        <SectionHeader title={t("ozet.sec.teslimat")} note={t("ozet.sec.teslimat_not")} />
        {teslimatQuery.data && teslimatQuery.data.haftalar.length > 0 ? (
          <div className="card mt-3" data-testid="teslimat-blok">
            <div className="mb-3">
              <TeslimatIstatistikCipleri
                genelOrtSaniye={teslimatQuery.data.ozet.genel_ort_saniye}
                olculenSiparis={teslimatQuery.data.ozet.olculen_siparis}
                sonHafta={teslimatQuery.data.haftalar[teslimatQuery.data.haftalar.length - 1]}
                veriKapsamiPct={teslimatQuery.data.ozet.veri_kapsami_pct}
              />
            </div>
            <TeslimatChart haftalar={teslimatQuery.data.haftalar} onHaftaSec={setTeslimatHaftaSecimi} />
            <p className="mt-1 text-center text-[11px] text-muted">{t("ozet.teslimat.grafik_ipucu")}</p>
          </div>
        ) : (
          <div className="mt-3">
            <EmptyState kind="no_data" />
          </div>
        )}
      </div>

      {/* 7) Kargo şirketi */}
      <div data-testid="ozet-kargo-sirketi">
        <SectionHeader title={t("ozet.sec.kargo")} note={t("ozet.kargo_sirketi.not")} />
        <div className="mt-3">
          <KargoSirketiGrid
            companies={kargoSirketiCompanies}
            onOpen={cardById("kargo_sirketi")?.state === "live" ? () => openDetail("kargo_sirketi", t("ozet.sec.kargo")) : undefined}
          />
        </div>
      </div>

      {/* 8) SGHV */}
      {kpis && <SghvKartlari sghv={(kpis as Record<string, unknown>).sghv} />}

      </div></details>

      <KpiDetailModal
        open={detailKpi != null}
        kpiId={detailKpi?.id ?? null}
        title={detailKpi?.title ?? ""}
        onClose={() => setDetailKpi(null)}
        onAnalyze={(rows) => {
          if (detailKpi) setAnalyzeTarget({ id: detailKpi.id, title: detailKpi.title, rows });
        }}
      />

      <AiAnalyzeModal
        open={analyzeTarget != null}
        title={analyzeTarget?.title ?? ""}
        kpiId={analyzeTarget?.id ?? null}
        days={period}
        modalRows={analyzeTarget?.rows}
        prompt={analyzeTarget?.prompt}
        onClose={() => setAnalyzeTarget(null)}
      />

      <TeslimatHaftaModal secim={teslimatHaftaSecimi} onClose={() => setTeslimatHaftaSecimi(null)} />

      <DestekIadeModal open={destekIadeOpen} title={t("ozet.destek_iade.baslik")} onClose={() => setDestekIadeOpen(false)} />
    </section>
  );
}
